import { Button, Center, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * The EMPTY state every data-backed surface owes the user: an icon, what this
 * kind of object *is* in their words, why they'd want one, and the action that
 * creates it. Replaces the bare `<Text c="dimmed">No X yet.</Text>` sentences
 * that leave a first-time vendor with nothing to click.
 *
 * The layout deliberately mirrors `PermissionGate`'s denied branch so the two
 * "nothing to show here" surfaces read as one system.
 */
export interface EmptyStateProps {
  icon: ReactNode;
  /** What the missing thing is, in the user's vocabulary. */
  title: string;
  /** Why they'd want one. One sentence. */
  description: string;
  action?: { label: string; onClick: () => void };
  /** Rendered under the action — e.g. a "learn more" link. */
  secondary?: ReactNode;
}

export function EmptyState({ icon, title, description, action, secondary }: EmptyStateProps) {
  return (
    <Center py="xl">
      <Stack align="center" gap="xs" maw={420} ta="center">
        <ThemeIcon size={56} radius="xl" variant="light" color="gray">
          {icon}
        </ThemeIcon>
        <Title order={5}>{title}</Title>
        <Text c="dimmed" size="sm">
          {description}
        </Text>
        {action && (
          <Button variant="light" mt="sm" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
        {secondary}
      </Stack>
    </Center>
  );
}
